import axios from 'axios';
import { AuthService } from './authService';
import { EnvironmentProvider } from '@chakra-ui/react';
import { method } from 'lodash';

/**
 * Enhanced API service for all backend communication including report triggered issues
 */
class ApiService {
    constructor() {
        this.baseURL = '';
        this.timeout = 30000;
        this.retryAttempts = 3;
        this.axiosInstance = null;
        this.requestInterceptor = null;
        this.responseInterceptor = null;
    }

    /**
     * Initialize API service with configuration and authentication
     * Sets up axios instance with interceptors for token management
     */
    initialize(config) {
        this.baseURL = config.api.baseUrl;
        this.timeout = config.api.timeout || 30000;
        this.retryAttempts = config.api.retryAttempts || 3;

        // Create axios instance with base configuration
        this.axiosInstance = axios.create({
            baseURL: this.baseURL,
            timeout: this.timeout,
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            }
        });

        this.setupInterceptors();
    }

    /**
     * Setup request and response interceptors
     * Handles authentication headers and error responses
     * To match backend CORS-allowed headers exactly
     */
    setupInterceptors() {
        // Request interceptor for authentication headers
        this.requestInterceptor = this.axiosInstance.interceptors.request.use(
            async (config) => {
                try {
                    // Add authentication headers
                    const token = await AuthService.getAccessToken();
                    if (token) {
                        config.headers.Authorization = `Bearer ${token}`;
                    }

                    // Add user context headers for backend authorization (CORS-allowed headers only)
                    const userEmail = localStorage.getItem('userEmail');
                    const userRole = localStorage.getItem('userRole');
                    
                    if (userRole) {
                        //config.headers['X-User-Role'] = userRole;
                        config.headers['X-User-Role'] = 'admin';
                    }
                    if (userEmail) {
                        config.headers['X-User-Email'] = userEmail;
                    }
                    
                    // Add X-Requested-With header (CORS-allowed)
                    config.headers['X-Requested-With'] = 'XMLHttpRequest';

                    return config;
                } catch (error) {
                    return Promise.reject(error);
                }
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        // Response interceptor for error handling and token refresh
        this.responseInterceptor = this.axiosInstance.interceptors.response.use(
            (response) => {
                return response;
            },
            async (error) => {
                const originalRequest = error.config;

                // Handle authentication errors with token refresh
                if (error.response?.status === 401 && !originalRequest._retry) {
                    originalRequest._retry = true;

                    try {
                        // Attempt token refresh
                        await AuthService.refreshToken();
                        
                        // Retry original request with new token
                        const token = await AuthService.getAccessToken();
                        if (token) {
                            originalRequest.headers.Authorization = `Bearer ${token}`;
                            return this.axiosInstance(originalRequest);
                        }
                    } catch (refreshError) {
                        // Redirect to login if refresh fails
                        AuthService.logout();
                        return Promise.reject(refreshError);
                    }
                }

                return Promise.reject(error);
            }
        );
    }

    /**
     * Extract Microsoft User ID from MSAL storage
     */
    getMicrosoftUserIdFromStorage() {
        try {
            // Check sessionStorage first (MSAL default), then localStorage as fallback
            const storages = [sessionStorage, localStorage];
            
            for (const storage of storages) {
                const msalKeys = Object.keys(storage).filter(key => 
                    key.startsWith('msal.') && 
                    (key.includes('.account.') || key.includes('accounts'))
                );
                
                // Try to find account data in storage
                for (const key of msalKeys) {
                    try {
                        const data = storage.getItem(key);
                        if (!data) continue;
                        
                        const parsedData = JSON.parse(data);
                        
                        // Handle different MSAL storage patterns
                        let microsoftUserId = null;
                        
                        // Pattern 1: Direct account object
                        if (parsedData.localAccountId) {
                            microsoftUserId = parsedData.localAccountId;
                        } else if (parsedData.homeAccountId) {
                            microsoftUserId = parsedData.homeAccountId;
                        } else if (parsedData.username) {
                            microsoftUserId = parsedData.username;
                        }
                        
                        // Pattern 2: Account collection
                        if (!microsoftUserId && Array.isArray(parsedData)) {
                            const account = parsedData.find(acc => acc && (acc.localAccountId || acc.homeAccountId || acc.username));
                            if (account) {
                                microsoftUserId = account.localAccountId || account.homeAccountId || account.username;
                            }
                        }
                        
                        // Pattern 3: Nested account structure
                        if (!microsoftUserId && parsedData.account) {
                            const account = parsedData.account;
                            microsoftUserId = account.localAccountId || account.homeAccountId || account.username;
                        }
                        
                        if (microsoftUserId) {
                            return microsoftUserId;
                        }
                        
                    } catch (parseError) {
                        console.warn('Failed to parse MSAL data for key:', key, parseError);
                        continue;
                    }
                }
            }
            
            // Pattern 4: AuthService current account fallback
            if (AuthService.currentAccount) {
                const microsoftUserId = AuthService.currentAccount.localAccountId || 
                                     AuthService.currentAccount.homeAccountId || 
                                     AuthService.currentAccount.username;
                if (microsoftUserId) {
                    return microsoftUserId;
                }
            }
            
            // Fallback: Try to get from localStorage userEmail
            const userEmail = localStorage.getItem('userEmail');
            if (userEmail) {
                return userEmail;
            }
            
            return null;
            
        } catch (error) {
            console.error('Failed to extract Microsoft User ID from storage:', error);
            return null;
        }
    }

    /**
     *  Add Microsoft User ID header to request config
     */
    addMicrosoftUserIdHeader(config, explicitMicrosoftUserId = null) {
        try {
            // Use explicit parameter first, then try to get from storage
            let microsoftUserId = explicitMicrosoftUserId || this.getMicrosoftUserIdFromStorage();
            
            if (microsoftUserId) {
                if (!config.headers) {
                    config.headers = {};
                }
                config.headers['X-Microsoft-User-Id'] = microsoftUserId;
            }
            
            return config;
            
        } catch (error) {
            console.error('Failed to add Microsoft User ID header:', error);
            return config;
        }
    }

    /**
     * Generic request method with retry logic
     * Handles network failures and implements exponential backoff
     */
    async makeRequest(config, retryCount = 0) {
        try {
            const response = await this.axiosInstance(config);
            return response.data;
        } catch (error) {
            // Retry logic for network errors
            if (retryCount < this.retryAttempts && this.shouldRetry(error)) {
                const delay = Math.pow(2, retryCount) * 1000; // Exponential backoff
                
                await new Promise(resolve => setTimeout(resolve, delay));
                return this.makeRequest(config, retryCount + 1);
            }
            
            throw this.handleError(error);
        }
    }

    /**
     * Determine if request should be retried
     * Retries network errors and specific HTTP status codes
     */
    shouldRetry(error) {
        // Don't retry authentication errors or client errors
        if (error.response?.status >= 400 && error.response?.status < 500) {
            return false;
        }
        
        // Retry network errors and server errors
        return !error.response || error.response.status >= 500;
    }

    /**
     * Standardize error handling across all API calls
     * Provides consistent error format for UI components
     */
    handleError(error) {
        let errorMessage = 'An unexpected error occurred';
        let errorCode = 'UNKNOWN_ERROR';
        let statusCode = 500;

        if (error.response) {
            // Server responded with error status
            statusCode = error.response.status;
            errorMessage = error.response.data?.message || error.response.statusText;
            errorCode = error.response.data?.code || `HTTP_${statusCode}`;
        } else if (error.request) {
            // Network error
            errorMessage = 'Network error - please check your connection';
            errorCode = 'NETWORK_ERROR';
        } else {
            // Request setup error
            errorMessage = error.message;
            errorCode = 'REQUEST_ERROR';
        }

        return {
            message: errorMessage,
            code: errorCode,
            status: statusCode,
            timestamp: new Date().toISOString()
        };
    }

    // ================================ REPORT TRIGGERED ISSUE API METHODS ================================

    /**
     * Get paginated list of report triggered issues with comprehensive filtering
     * Matches: GET /cas/report-triggered
     */
    async getReportTriggeredIssues(params = {}) {
    return this.makeRequest({
        method: 'GET',
        url: '/cas/report-triggered',
        params: {
            page: params.page || 1,
            size: params.size || 20,
            sortBy: params.sortBy || 'created_at_utc',
            sortDirection: params.sortDirection || 'DESC',
            search: params.search,
            client_id: params.client_id,
            report_code: params.report_code,
            is_issue_closed: params.is_issue_closed,
            fixed_by: params.fixed_by,
            not_an_issue_by: params.not_an_issue_by,
            issue_taken_by: params.issue_taken_by,
            startDate: params.startDate,
            endDate: params.endDate,
            exclude_internal: params.exclude_internal,
            // Tickets tab: restrict to issues that have a Redmine ticket, optionally by
            // whether that ticket is closed.
            has_ticket: params.has_ticket,
            ticket_closed: params.ticket_closed
        }
    });
}

    /**
     * Get specific report triggered issue by ID
     * Matches: GET /cas/report-triggered/{issueId}
     */
    async getReportTriggeredIssueById(issueId) {
        if (!issueId) {
            throw new Error('Issue ID is required');
        }
        return this.makeRequest({
            method: 'GET',
            url: `/cas/report-triggered/${issueId}`
        });
    }

    /**
     * Get issue count by client for dashboard analytics
     * Matches: GET /cas/report-triggered/count-by-client
     */
    async getIssueCountByClient() {
        return this.makeRequest({
            method: 'GET',
            url: '/cas/report-triggered/count-by-client'
        });
    }

    /**
     * Get count of taken issues for team workload metrics
     * Matches: GET /cas/report-triggered/taken-count
     */
    /**
     * Send a batch of tracked usage events.
     * Matches: POST /cas/usage/track
     * The acting user is taken from the X-User-Email header server-side, not the body.
     */
    async trackUsage(events = []) {
        if (!Array.isArray(events) || events.length === 0) return { success: true };
        return this.makeRequest({
            method: 'POST',
            url: '/cas/usage/track',
            data: { events }
        });
    }

    /**
     * Per-user usage summary for a window.
     * Matches: GET /cas/usage/stats
     */
    async getUsageStats(params = {}) {
        return this.makeRequest({
            method: 'GET',
            url: '/cas/usage/stats',
            params: {
                startDate: params.startDate,
                endDate: params.endDate
            }
        });
    }

    /**
     * Module/action breakdown for one user.
     * Matches: GET /cas/usage/stats/actions
     */
    async getUsageActionBreakdown(email, params = {}) {
        if (!email) throw new Error('Email is required');
        return this.makeRequest({
            method: 'GET',
            url: '/cas/usage/stats/actions',
            params: {
                email,
                startDate: params.startDate,
                endDate: params.endDate
            }
        });
    }

    /**
     * Everything the usage Excel export needs, in one call.
     * Matches: GET /cas/usage/stats/export
     * Returns { startDate, endDate, users: [...], modules: [...], moduleActions: [...] }
     */
    async getUsageExportData(params = {}) {
        return this.makeRequest({
            method: 'GET',
            url: '/cas/usage/stats/export',
            params: {
                startDate: params.startDate,
                endDate: params.endDate
            }
        });
    }

    async getTakenIssueCount() {
        return this.makeRequest({
            method: 'GET',
            url: '/cas/report-triggered/taken-count'
        });
    }

    /**
     * Count of issues that have a Redmine ticket which is not closed.
     * A ticket that has never been synced counts as open, matching the Tickets tab.
     *
     * Reuses the list endpoint with size=1 and reads the pagination total, so no extra
     * backend endpoint is needed. Returns the usual { success, data, message } shape.
     */
    async getOpenTicketCount() {
        const response = await this.getReportTriggeredIssues({
            page: 1,
            size: 1,
            has_ticket: true,
            ticket_closed: false
        });
        return {
            success: !!(response && response.success),
            data: { openTicketCount: response?.data?.pagination?.total || 0 },
            message: response?.message
        };
    }

    /**
     * Create new report triggered issue
     * Matches: POST /cas/report-triggered
     */
    async createReportTriggeredIssue(issueData) {
        if (!issueData || !issueData.clientId || !issueData.reportCode || !issueData.message) {
            throw new Error('Client ID, report code, and message are required');
        }
        return this.makeRequest({
            method: 'POST',
            url: '/cas/report-triggered',
            data: {
                clientId: issueData.clientId,
                reportCode: issueData.reportCode,
                message: issueData.message
            }
        });
    }

    /**
     * Assign issue to user for resolution
     * Matches: PUT /cas/report-triggered/{issueId}/assign
     */
    async assignReportTriggeredIssue(issueId, issueTakenBy) {
        if (!issueId || !issueTakenBy) {
            throw new Error('Issue ID and assignee email are required');
        }
        return this.makeRequest({
            method: 'PUT',
            url: `/cas/report-triggered/${issueId}/assign`,
            data: {
                issueTakenBy: issueTakenBy
            }
        });
    }

    /**
     * List all comments for an issue.
     * Matches: GET /cas/report-triggered/{issueId}/comments
     */
    async getIssueComments(issueId) {
        if (!issueId) throw new Error('Issue ID is required');
        return this.makeRequest({
            method: 'GET',
            url: `/cas/report-triggered/${issueId}/comments`
        });
    }

    /**
     * Add a comment to an issue.
     * Matches: POST /cas/report-triggered/{issueId}/comments
     */
    async addIssueComment(issueId, comment, createdBy) {
        if (!issueId || !comment) throw new Error('Issue ID and comment are required');
        return this.makeRequest({
            method: 'POST',
            url: `/cas/report-triggered/${issueId}/comments`,
            data: { comment, createdBy }
        });
    }

    /**
     * Edit an existing comment on an issue.
     * Matches: PUT /cas/report-triggered/{issueId}/comments/{commentId}
     */
    async updateIssueComment(issueId, commentId, comment) {
        if (!issueId || !commentId || !comment) throw new Error('Issue ID, comment ID and comment are required');
        return this.makeRequest({
            method: 'PUT',
            url: `/cas/report-triggered/${issueId}/comments/${commentId}`,
            data: { comment }
        });
    }

    /**
     * Pick-lists for the create-ticket form (trackers, priorities, project members).
     * Matches: GET /cas/report-triggered/{issueId}/redmine-options
     */
    async getRedmineOptions(issueId) {
        if (!issueId) throw new Error('Issue ID is required');
        return this.makeRequest({
            method: 'GET',
            url: `/cas/report-triggered/${issueId}/redmine-options`
        });
    }

    /**
     * Create a Redmine ticket for an issue (in the client's Redmine project).
     * Matches: POST /cas/report-triggered/{issueId}/redmine
     * @param {object} payload { subject, trackerId, priorityId, assignedToId, description }
     */
    async createRedmineTicket(issueId, payload = {}) {
        if (!issueId) throw new Error('Issue ID is required');
        return this.makeRequest({
            method: 'POST',
            url: `/cas/report-triggered/${issueId}/redmine`,
            data: {
                subject: payload.subject || '',
                trackerId: payload.trackerId ?? null,
                priorityId: payload.priorityId ?? null,
                assignedToId: payload.assignedToId ?? null,
                description: payload.description || '',
                customFields: payload.customFields || []
            }
        });
    }

    /**
     * Re-read the current status of an issue's Redmine ticket and cache it server-side.
     * Matches: POST /cas/report-triggered/{issueId}/redmine/sync
     * Returns { redmineIssueId, redmineStatus, redmineStatusIsClosed,
     *           redmineStatusSyncedAtUtc, ticketMissing }
     */
    async syncRedmineStatus(issueId) {
        if (!issueId) throw new Error('Issue ID is required');
        return this.makeRequest({
            method: 'POST',
            url: `/cas/report-triggered/${issueId}/redmine/sync`
        });
    }

    /**
     * Mark issue as fixed
     * Matches: PUT /cas/report-triggered/{issueId}/fix
     */
    async markReportTriggeredIssueAsFixed(issueId, fixedBy) {
        if (!issueId || !fixedBy) {
            throw new Error('Issue ID and fixer email are required');
        }
        return this.makeRequest({
            method: 'PUT',
            url: `/cas/report-triggered/${issueId}/fix`,
            data: {
                fixedBy: fixedBy
            }
        });
    }

    /**
     * Mark issue as not an issue
     * Matches: PUT /cas/report-triggered/{issueId}/not-issue
     */
    async markReportTriggeredIssueAsNotAnIssue(issueId, notAnIssueBy) {
        if (!issueId || !notAnIssueBy) {
            throw new Error('Issue ID and determiner email are required');
        }
        return this.makeRequest({
            method: 'PUT',
            url: `/cas/report-triggered/${issueId}/not-issue`,
            data: {
                notAnIssueBy: notAnIssueBy
            }
        });
    }

    /**
     * Reopen closed issue
     * Matches: PUT /cas/report-triggered/{issueId}/reopen
     */
    async reopenReportTriggeredIssue(issueId) {
        if (!issueId) {
            throw new Error('Issue ID is required');
        }
        return this.makeRequest({
            method: 'PUT',
            url: `/cas/report-triggered/${issueId}/reopen`
        });
    }

    /**
     * Delete report triggered issue (soft delete)
     * Matches: DELETE /cas/report-triggered/{issueId}
     */
    async deleteReportTriggeredIssue(issueId) {
        if (!issueId) {
            throw new Error('Issue ID is required');
        }
        return this.makeRequest({
            method: 'DELETE',
            url: `/cas/report-triggered/${issueId}`
        });
    }

    // ================================ EXISTING USER MANAGEMENT API METHODS ================================

    /**
     * Get paginated list of users with search and filtering
     * Matches: GET /cas/user/
     */
    async getUsers(params = {}) {
        return this.makeRequest({
            method: 'GET',
            url: '/cas/user/',
            params: {
                page: params.page || 1,
                size: params.size || 20,
                search: params.search,
                status: params.status,
                role: params.role,
                sortBy: params.sortBy || 'created_at_utc',
                sortOrder: params.sortOrder || 'DESC'
            }
        });
    }

    /**
     * Get user by ID
     * Matches: GET /cas/user/id/{id}
     */
    async getUserById(userId) {
        if (!userId) {
            throw new Error('User ID is required');
        }
        return this.makeRequest({
            method: 'GET',
            url: `/cas/user/id/${userId}`
        });
    }

    /**
     * Get user by email address
     * Matches: GET /cas/user/email/{email}
     */
    async getUserByEmail(email) {
        if (!email) {
            throw new Error('Email is required');
        }
        return this.makeRequest({
            method: 'GET',
            url: `/cas/user/email/${encodeURIComponent(email)}`
        });
    }

    /**
     * Get user by Microsoft user ID
     * Matches: GET /cas/user/microsoft/{microsoftUserId}
     */
    async getUserByMicrosoftId(microsoftUserId) {
        if (!microsoftUserId) {
            throw new Error('Microsoft User ID is required');
        }
        return this.makeRequest({
            method: 'GET',
            url: `/cas/user/microsoft/${microsoftUserId}`
        });
    }

    /**
     * Get users by status
     * Matches: GET /cas/user/status
     */
    async getUsersByStatus(status, params = {}) {
        if (!status) {
            throw new Error('Status is required');
        }
        return this.makeRequest({
            method: 'GET',
            url: '/cas/user/status',
            params: {
                status: status,
                page: params.page || 1,
                size: params.size || 20
            }
        });
    }

    /**
     * Get user statistics for dashboard
     * Matches: GET /cas/user/statistics
     */
    async getUserStatistics() {
        return this.makeRequest({
            method: 'GET',
            url: '/cas/user/statistics'
        });
    }

    /**
     * Get user timeline data
     * Matches: GET /cas/user/timeline
     */
    async getUserTimeline(params = {}) {
        return this.makeRequest({
            method: 'GET',
            url: '/cas/user/timeline',
            params: {
                startDate: params.startDate,
                endDate: params.endDate
            }
        });
    }

    /**
     * Get user audit logs
     * Matches: GET /cas/user/audit
     */
    async getUserAuditLogs(params = {}) {
        return this.makeRequest({
            method: 'GET',
            url: '/cas/user/audit',
            params: {
                page: params.page || 1,
                size: params.size || 20,
                userId: params.userId,
                action: params.action,
                startDate: params.startDate,
                endDate: params.endDate,
                search: params.search
            }
        });
    }

    /**
     * Get available clients for user assignment
     * Matches: GET /cas/user/clients
     */
    async getAvailableClients() {
        return this.makeRequest({
            method: 'GET',
            url: '/cas/user/clients'
        });
    }

    /**
     * Authenticate user with Microsoft SSO profile
     * Matches: POST /cas/user/authenticate
     */
    async authenticateUser(microsoftProfile, ipAddress, userAgent) {
        if (!microsoftProfile) {
            throw new Error('Microsoft profile is required');
        }
        return this.makeRequest({
            method: 'POST',
            url: '/cas/user/authenticate',
            data: {
                microsoftProfile: microsoftProfile,
                ipAddress: ipAddress,
                userAgent: userAgent
            }
        });
    }

    /**
     * Create new user
     * Matches: POST /cas/user/
     */
    async createUser(userData) {
        if (!userData || !userData.email) {
            throw new Error('User email is required');
        }
        return this.makeRequest({
            method: 'POST',
            url: '/cas/user/',
            data: userData
        });
    }

    /**
     * Update existing user
     * Matches: PUT /cas/user/{id}
     */
    async updateUser(userId, userData) {
        if (!userId) {
            throw new Error('User ID is required');
        }
        return this.makeRequest({
            method: 'PUT',
            url: `/cas/user/${userId}`,
            data: userData
        });
    }

    /**
     * Update user status
     * Matches: PUT /cas/user/{id}/status
     */
    async updateUserStatus(userId, status, comments = '') {
        if (!userId || !status) {
            throw new Error('User ID and status are required');
        }
        return this.makeRequest({
            method: 'PUT',
            url: `/cas/user/${userId}/status`,
            data: {
                status: status,
                comments: comments
            }
        });
    }

    /**
     * Update user role
     * Matches: PUT /cas/user/{id}/role
     */
    async updateUserRole(userId, role) {
        if (!userId || !role) {
            throw new Error('User ID and role are required');
        }
        return this.makeRequest({
            method: 'PUT',
            url: `/cas/user/${userId}/role`,
            data: {
                role: role
            }
        });
    }

    /**
     * Update user client group
     * Matches: PUT /cas/user/{id}/client-group
     */
    async updateUserClientGroup(userId, clientGroup) {
        if (!userId) {
            throw new Error('User ID is required');
        }
        return this.makeRequest({
            method: 'PUT',
            url: `/cas/user/${userId}/client-group`,
            data: {
                clientGroup: clientGroup
            }
        });
    }

    /**
     * Delete user
     * Matches: DELETE /cas/user/{id}
     */
    async deleteUser(userId) {
        if (!userId) {
            throw new Error('User ID is required');
        }
        return this.makeRequest({
            method: 'DELETE',
            url: `/cas/user/${userId}`
        });
    }

    /**
     * Record user login
     * Matches: POST /cas/user/login
     */
    async recordUserLogin(userId, sessionId) {
        if (!userId) {
            throw new Error('User ID is required');
        }
        return this.makeRequest({
            method: 'POST',
            url: '/cas/user/login',
            data: {
                userId: userId,
                sessionId: sessionId
            }
        });
    }

    /**
     * Record user logout
     * Matches: POST /cas/user/logout
     */
    async recordUserLogout(userId, sessionId) {
        if (!userId) {
            throw new Error('User ID is required');
        }
        return this.makeRequest({
            method: 'POST',
            url: '/cas/user/logout',
            data: {
                userId: userId,
                sessionId: sessionId
            }
        });
    }

    /**
     * Bulk user operations
     * Matches: POST /cas/user/bulk
     */
    async bulkUserOperation(userIds, operation, comments = '') {
        if (!userIds || userIds.length === 0 || !operation) {
            throw new Error('User IDs and operation are required');
        }
        return this.makeRequest({
            method: 'POST',
            url: '/cas/user/bulk',
            data: {
                userIds: userIds,
                operation: operation,
                comments: comments
            }
        });
    }

    /**
     * Perform user maintenance operations
     * Matches: POST /cas/user/maintenance
     */
    async performUserMaintenance(operationType, parameters = {}) {
        if (!operationType) {
            throw new Error('Operation type is required');
        }
        return this.makeRequest({
            method: 'POST',
            url: '/cas/user/maintenance',
            data: {
                operationType: operationType,
                parameters: parameters
            }
        });
    }

    // ================================ EXISTING CLIENT API METHODS ================================

    /**
     * Get paginated list of clients with search and filtering
     * Matches: GET /cas/client/list
     */
    async getClientsList(params = {}) {
        return this.makeRequest({
            method: 'GET',
            url: '/cas/client/list',
            params: {
                page: params.page || 1,
                size: params.size || 20,
                sortBy: params.sortBy,
                sortDirection: params.sortDirection,
                search: params.search
            }
        });
    }

    /**
     * Get client by ID
     * Matches: GET /cas/client/{id}
     */
    async getClientById(clientId) {
        if (!clientId) {
            throw new Error('Client ID is required');
        }
        return this.makeRequest({
            method: 'GET',
            url: `/cas/client/${clientId}`
        });
    }

    /**
     * Get clients by status with pagination
     * Matches: GET /cas/client/status/{status}
     */
    async getClientsByStatus(status, params = {}) {
        if (!status) {
            throw new Error('Status is required');
        }
        return this.makeRequest({
            method: 'GET',
            url: `/cas/client/status/${status}`,
            params: {
                page: params.page || 1,
                size: params.size || 20,
                search: params.search
            }
        });
    }

    /**
     * Get all active clients (no pagination)
     * Matches: GET /cas/client/active
     */
    async getActiveClients() {
        return this.makeRequest({
            method: 'GET',
            url: '/cas/client/active'
        });
    }

    /**
     * Get online clients for monitoring
     * Matches: GET /cas/client/online
     */
    async getOnlineClients() {
        return this.makeRequest({
            method: 'GET',
            url: '/cas/client/online'
        });
    }

    /**
     * Get offline clients for monitoring
     * Matches: GET /cas/client/offline
     */
    async getOfflineClients() {
        return this.makeRequest({
            method: 'GET',
            url: '/cas/client/offline'
        });
    }

    /**
     * Get client statistics for dashboard
     * Matches: GET /cas/client/statistics
     */
    async getClientStatistics() {
        return this.makeRequest({
            method: 'GET',
            url: '/cas/client/statistics'
        });
    }

    /**
     * Get clients not mapped to reports
     * Matches: GET /cas/client/unmapped
     */
    async getUnmappedClients() {
        return this.makeRequest({
            method: 'GET',
            url: '/cas/client/unmapped'
        });
    }

    /**
     * Search clients by name
     * Matches: GET /cas/client/search?q=term&limit=maxResults
     */
    async searchClients(searchTerm, maxResults = 20) {
        if (!searchTerm) {
            throw new Error('Search term is required');
        }
        return this.makeRequest({
            method: 'GET',
            url: '/cas/client/search',
            params: {
                q: searchTerm,
                limit: maxResults
            }
        });
    }

    /**
     * Create new client (Admin only)
     * Matches: POST /cas/client
     */
    async createClient(clientData) {
        if (!clientData || !clientData.clientName || !clientData.clientSecret) {
            throw new Error('Client name and client secret are required');
        }
        return this.makeRequest({
            method: 'POST',
            url: '/cas/client',
            data: {
                clientName: clientData.clientName,
                clientSecret: clientData.clientSecret,
                clientStatus: clientData.clientStatus || 'Offline',
                clientReportPerson: clientData.clientReportPerson,
                prismVersion:clientData.prismVersion,
                baseUrl:clientData.baseUrl,
                sendChecklistAlerts: clientData.sendChecklistAlerts ? 1 : 0,
                emtDelayEnabled: clientData.emtDelayEnabled ? 1 : 0,
                isInternal: clientData.isInternal ? 1 : 0,
                redmineProjectId: clientData.redmineProjectId || null,
                marketDataClient1:clientData.marketDataClient1,
                marketDataClient2:clientData.marketDataClient2,
            }
        });
    }

    /**
     * Update existing client (Admin only)
     * Matches: PUT /cas/client/{id}
     */
    async updateClient(clientId, clientData) {
        if (!clientId) {
            throw new Error('Client ID is required');
        }
        return this.makeRequest({
            method: 'PUT',
            url: `/cas/client/${clientId}`,
            data: {
                clientName: clientData.clientName,
                clientSecret: clientData.clientSecret,
                clientStatus: clientData.clientStatus,
                clientReportPerson:clientData.clientReportPerson,
                prismVersion:clientData.prismVersion,
                baseUrl:clientData.baseUrl,
                sendChecklistAlerts: clientData.sendChecklistAlerts ? 1 : 0,
                emtDelayEnabled: clientData.emtDelayEnabled ? 1 : 0,
                isInternal: clientData.isInternal ? 1 : 0,
                redmineProjectId: clientData.redmineProjectId || null,
                marketDataClient1:clientData.marketDataClient1,
                marketDataClient2:clientData.marketDataClient2,
            }
        });
    }

    /**
     * Delete client (Admin only)
     * Matches: DELETE /cas/client/{id}
     */
    async deleteClient(clientId) {
        if (!clientId) {
            throw new Error('Client ID is required');
        }
        return this.makeRequest({
            method: 'DELETE',
            url: `/cas/client/${clientId}`
        });
    }

    /**
     * Update client status (Admin only)
     * Matches: PUT /cas/client/{id}/status
     */
    async updateClientStatus(clientId, status) {
        if (!clientId || !status) {
            throw new Error('Client ID and status are required');
        }
        return this.makeRequest({
            method: 'PUT',
            url: `/cas/client/${clientId}/status`,
            data: { status }
        });
    }

    /**
     * Batch update client statuses (Admin only)
     * Matches: POST /cas/client/batch/status
     */
    async batchUpdateClientStatus(clientIds, status) {
        if (!clientIds || clientIds.length === 0 || !status) {
            throw new Error('Client IDs and status are required');
        }
        return this.makeRequest({
            method: 'POST',
            url: '/cas/client/batch/status',
            data: {
                clientIds,
                status
            }
        });
    }

    /**
     * Enable maintenance mode (Admin only)
     * Matches: PUT /cas/client/{id}/maintenance
     */
    async enableMaintenanceMode(clientId) {
        if (!clientId) {
            throw new Error('Client ID is required');
        }
        return this.makeRequest({
            method: 'PUT',
            url: `/cas/client/${clientId}/maintenance`,
            data: { enable: true }
        });
    }

    /**
     * Disable maintenance mode (Admin only)
     * Matches: PUT /cas/client/{id}/maintenance
     */
    async disableMaintenanceMode(clientId) {
        if (!clientId) {
            throw new Error('Client ID is required');
        }
        return this.makeRequest({
            method: 'PUT',
            url: `/cas/client/${clientId}/maintenance`,
            data: { enable: false }
        });
    }

    /**
     * Process client heartbeat (System operation)
     * Matches: POST /cas/client/{id}/heartbeat
     */
    async processClientHeartbeat(clientId) {
        if (!clientId) {
            throw new Error('Client ID is required');
        }
        return this.makeRequest({
            method: 'POST',
            url: `/cas/client/${clientId}/heartbeat`
        });
    }

    async getClients(params = {}) {
        return this.getClientsList(params);
    }

    // ================================ DASHBOARD API METHODS ================================

    async getDashboardStats() {
        return this.makeRequest({
            method: 'GET',
            url: '/cas/dashboard/stats'
        });
    }

    async getHeartbeatStatus() {
        return this.makeRequest({
            method: 'GET',
            url: '/cas/dashboard/heartbeat'
        });
    }

    async getReportResults() {
        return this.makeRequest({
            method: 'GET',
            url: '/cas/dashboard/report-results'
        });
    }

    // ================================ REPORT API METHODS ================================

    async getReports(params = {}) {
        return this.makeRequest({
            method: 'GET',
            url: '/cas/report/',
            params: {
                page: params.page || 1,
                size: params.size || 20,
                sortBy: params.sortBy,
                sortDirection: params.sortDirection,
                search: params.search
            }
        });
    }

    async getReportByCode(reportCode) {
        if (!reportCode) {
            throw new Error('Report code is required');
        }
        return this.makeRequest({
            method: 'GET',
            url: `/cas/report/${reportCode}`
        });
    }

    async getReportByModule(reportModule){
        if(!reportModule){
            throw new Error(" Report Module Required ");
        }
        return this.makeRequest({
            method: 'GET',
            url:`cas/report/getReport?reportModule=${reportModule}`
        })
    }

    // Report module master (CAS_REPORT_MODULE) — drives the module dropdown
    async getReportModules(){
        return this.makeRequest({
            method: 'GET',
            url: '/cas/report/modules'
        });
    }

    async createReportModule(data){
        return this.makeRequest({
            method: 'POST',
            url: '/cas/report/modules',
            data: { name: data.name, value: data.value }
        });
    }

    async getScheduledReports() {
        return this.makeRequest({
            method: 'GET',
            url: '/cas/report/scheduled'
        });
    }

    async getUnmappedReports() {
        return this.makeRequest({
            method: 'GET',
            url: '/cas/report/unmapped'
        });
    }

    async getReportStatistics() {
        return this.makeRequest({
            method: 'GET',
            url: '/cas/report/statistics'
        });
    }

    async exportReportDefinition(reportCode) {
        if (!reportCode) {
            throw new Error('Report code is required');
        }
        return this.makeRequest({
            method: 'GET',
            url: `/cas/report/${reportCode}/export`
        });
    }

    async getReportExecutionSummary(reportCode) {
        if (!reportCode) {
            throw new Error('Report code is required');
        }
        return this.makeRequest({
            method: 'GET',
            url: `/cas/report/${reportCode}/execution-summary`
        });
    }

    async createReport(reportData) {
        if (!reportData || !reportData.report || !reportData.reportDescription || !reportData.alertMessage) {
            throw new Error('Report SQL, description, and alert message are required');
        }
        return this.makeRequest({
            method: 'POST',
            url: '/cas/report/',
            data: {
                report: reportData.report,
                reportDescription: reportData.reportDescription,
                alertMessage: reportData.alertMessage,
                parameters: reportData.parameters || [],
                isEnabled: reportData.isEnabled !== undefined ? reportData.isEnabled : true,
                cronExpression: reportData.cronExpression,
                isCronEnabled: reportData.isCronEnabled || false,
                createdBy: reportData.createdBy,
                isTeamsAlertEnabled:reportData.isTeamsAlertEnabled!==undefined ? reportData.isTeamsAlertEnabled: true,
                reportType:reportData.reportType,
                isAutoCloseIssues: reportData.isAutoCloseIssues,
                reportModule: reportData.reportModule,
                dataQuery: reportData.dataQuery,
                alertRepeatEnabled:reportData.alertRepeatEnabled,
            }
        });
    }

    async updateReport(reportCode, reportData) {
        if (!reportCode) {
            throw new Error('Report code is required');
        }
        return this.makeRequest({
            method: 'PUT',
            url: `/cas/report/${reportCode}`,
            data: {
                report: reportData.report,
                reportDescription: reportData.reportDescription,
                alertMessage: reportData.alertMessage,
                parameters: reportData.parameters || [],
                isEnabled: reportData.isEnabled,
                cronExpression: reportData.cronExpression,
                isCronEnabled: reportData.isCronEnabled,
                updatedBy: reportData.updatedBy,
                isTeamsAlertEnabled:reportData.isTeamsAlertEnabled,
                reportType: reportData.reportType,
                isAutoCloseIssues:reportData.isAutoCloseIssues,
                reportModule: reportData.reportModule,
                dataQuery: reportData.dataQuery,
                alertRepeatEnabled:reportData.alertRepeatEnabled,
            }
        });
    }

    async deleteReport(reportCode) {
        if (!reportCode) {
            throw new Error('Report code is required');
        }
        return this.makeRequest({
            method: 'DELETE',
            url: `/cas/report/${reportCode}`
        });
    }

    async importReportDefinition(reportDefinition) {
        if (!reportDefinition) {
            throw new Error('Report definition is required');
        }
        return this.makeRequest({
            method: 'POST',
            url: '/cas/report/import',
            data: {
                reportDefinition: reportDefinition
            }
        });
    }

    async batchDeleteReports(reportCodes) {
        if (!reportCodes || reportCodes.length === 0) {
            throw new Error('Report codes are required');
        }
        return this.makeRequest({
            method: 'POST',
            url: '/cas/report/batch-delete',
            data: {
                reportCodes: reportCodes
            }
        });
    }

    async toggleReportStatus(reportCode, enabled) {
        if (!reportCode) {
            throw new Error('Report code is required');
        }
        return this.makeRequest({
            method: 'POST',
            url: `/cas/report/${reportCode}/toggle-status`,
            data: {
                enabled: enabled
            }
        });
    }

    async toggleReportScheduling(reportCode, cronEnabled) {
        if (!reportCode) {
            throw new Error('Report code is required');
        }
        return this.makeRequest({
            method: 'POST',
            url: `/cas/report/${reportCode}/toggle-scheduling`,
            data: {
                cronEnabled: cronEnabled
            }
        });
    }

    // ================================ REPORT MAPPING API METHODS ================================

    async getReportMappingsList(params = {}) {
        // Build query params, forwarding all server-side filters. Only defined,
        // non-"all" values are included so the backend applies no constraint for
        // omitted filters (axios drops undefined params automatically).
        const queryParams = {
            page: params.page || 1,
            size: params.size || 20,
            sortBy: params.sortBy || 'created_at_utc',
            sortDirection: params.sortDirection || 'DESC'
        };

        if (params.search) queryParams.search = params.search;
        if (params.clientId !== undefined && params.clientId !== null && params.clientId !== 'all') {
            queryParams.clientId = params.clientId;
        }
        if (params.reportCode !== undefined && params.reportCode !== null && params.reportCode !== 'all') {
            queryParams.reportCode = params.reportCode;
        }
        if (params.reportModule && params.reportModule !== 'all') {
            queryParams.reportModule = params.reportModule;
        }
        if (params.isEnabled !== undefined) queryParams.isEnabled = params.isEnabled;
        if (params.isVerified !== undefined) queryParams.isVerified = params.isVerified;
        if (params.isVerificationError !== undefined) queryParams.isVerificationError = params.isVerificationError;
        if (params.isCronTriggered !== undefined) queryParams.isCronTriggered = params.isCronTriggered;

        return this.makeRequest({
            method: 'GET',
            url: '/cas/report/client-report/map/',
            params: queryParams
        });
    }

    async getAllReportMappings(params = {}) {
        // Returns every mapping matching the filters (no pagination) for the pivot view.
        const queryParams = {};
        if (params.search) queryParams.search = params.search;
        if (params.clientId !== undefined && params.clientId !== null && params.clientId !== 'all') {
            queryParams.clientId = params.clientId;
        }
        if (params.reportCode !== undefined && params.reportCode !== null && params.reportCode !== 'all') {
            queryParams.reportCode = params.reportCode;
        }
        if (params.reportModule && params.reportModule !== 'all') {
            queryParams.reportModule = params.reportModule;
        }
        if (params.isEnabled !== undefined) queryParams.isEnabled = params.isEnabled;
        if (params.isVerified !== undefined) queryParams.isVerified = params.isVerified;
        if (params.isVerificationError !== undefined) queryParams.isVerificationError = params.isVerificationError;
        if (params.isCronTriggered !== undefined) queryParams.isCronTriggered = params.isCronTriggered;

        return this.makeRequest({
            method: 'GET',
            url: '/cas/report/client-report/map/all',
            params: queryParams
        });
    }

    /**
     * Pivot "Not Applicable" markers: list all active client x report N/A pairs.
     * These are annotations only — they do not create mappings or trigger execution.
     */
    async getReportMappingNAList() {
        return this.makeRequest({
            method: 'GET',
            url: '/cas/report/client-report/map/na'
        });
    }

    /**
     * Mark a client x report pair as Not Applicable in the pivot.
     */
    async markReportMappingNA(clientId, reportCode) {
        return this.makeRequest({
            method: 'POST',
            url: '/cas/report/client-report/map/na',
            data: { clientId, reportCode }
        });
    }

    /**
     * Remove a Not Applicable marker (revert the pivot cell back to "Add").
     */
    async removeReportMappingNA(clientId, reportCode) {
        return this.makeRequest({
            method: 'POST',
            url: '/cas/report/client-report/map/na-remove',
            data: { clientId, reportCode }
        });
    }

    async getReportMappingsByClient(clientId, params = {}) {
        if (!clientId) {
            throw new Error('Client ID is required');
        }
        return this.makeRequest({
            method: 'GET',
            url: '/cas/report/client-report/map/client',
            params: {
                clientId: clientId,
                page: params.page || 1,
                size: params.size || 20,
                search: params.search
            }
        });
    }

    async getReportMappingsByReport(reportCode, params = {}) {
        if (!reportCode) {
            throw new Error('Report code is required');
        }
        return this.makeRequest({
            method: 'GET',
            url: '/cas/report/client-report/map/report',
            params: {
                reportCode: reportCode,
                page: params.page || 1,
                size: params.size || 20,
                search: params.search
            }
        });
    }

    async getReportMapping(clientId, reportCode) {
        if (!clientId || !reportCode) {
            throw new Error('Client ID and Report Code are required');
        }
        return this.makeRequest({
            method: 'GET',
            url: `/cas/report/client-report/map/${clientId}/${reportCode}`
        });
    }

    async getReportMappingStatistics(params = {}) {
        // Forward the same filters as the list so the summary cards reflect the
        // active filters. Only defined, non-"all" values are sent.
        const queryParams = {};
        if (params.search) queryParams.search = params.search;
        if (params.clientId !== undefined && params.clientId !== null && params.clientId !== 'all') {
            queryParams.clientId = params.clientId;
        }
        if (params.reportCode !== undefined && params.reportCode !== null && params.reportCode !== 'all') {
            queryParams.reportCode = params.reportCode;
        }
        if (params.reportModule && params.reportModule !== 'all') {
            queryParams.reportModule = params.reportModule;
        }
        if (params.isEnabled !== undefined) queryParams.isEnabled = params.isEnabled;
        if (params.isVerified !== undefined) queryParams.isVerified = params.isVerified;
        if (params.isVerificationError !== undefined) queryParams.isVerificationError = params.isVerificationError;
        if (params.isCronTriggered !== undefined) queryParams.isCronTriggered = params.isCronTriggered;

        return this.makeRequest({
            method: 'GET',
            url: '/cas/report/client-report/map/statistics',
            params: queryParams
        });
    }

    async getUnverifiedMappings(clientId) {
        if (!clientId) {
            throw new Error('Client ID is required');
        }
        return this.makeRequest({
            method: 'GET',
            url: '/cas/report/client-report/map/unverified',
            params: { clientId }
        });
    }

    async getCronTriggeredMappings(clientId) {
        if (!clientId) {
            throw new Error('Client ID is required');
        }
        return this.makeRequest({
            method: 'GET',
            url: '/cas/report/client-report/map/cron-triggered',
            params: { clientId }
        });
    }

    async getClientsByReport(reportCode) {
        if (!reportCode) {
            throw new Error('Report code is required');
        }
        return this.makeRequest({
            method: 'GET',
            url: '/cas/report/client-report/map/clients-by-report',
            params: { reportCode }
        });
    }

    async getReportsByClient(clientId) {
        if (!clientId) {
            throw new Error('Client ID is required');
        }
        return this.makeRequest({
            method: 'GET',
            url: '/cas/report/client-report/map/reports-by-client',
            params: { clientId }
        });
    }

    async createReportMapping(mappingData) {
        if (!mappingData || !mappingData.reportCode || !mappingData.clientId) {
            throw new Error('Report code and client ID are required');
        }
        
        const requestData = {
            reportCode: Number(mappingData.reportCode),
            clientId: Number(mappingData.clientId),
            isEnabled: mappingData.isEnabled !== undefined ? Boolean(mappingData.isEnabled) : true,
            createdBy: mappingData.createdBy || localStorage.getItem('userEmail') || 'admin@emaildashboard.com'
        };

        return this.makeRequest({
            method: 'POST',
            url: '/cas/report/client-report/map/',
            data: requestData
        });
    }

    async createBatchReportMappings(mappingData) {
        if (!mappingData || !mappingData.reportCode || !mappingData.clientIds) {
            throw new Error('Report code and client IDs are required');
        }
        
        const requestData = {
            reportCode: Number(mappingData.reportCode),
            clientIds: Array.isArray(mappingData.clientIds) 
                ? mappingData.clientIds.map(id => Number(id))
                : [Number(mappingData.clientIds)],
            isEnabled: mappingData.isEnabled !== undefined ? Boolean(mappingData.isEnabled) : true,
            createdBy: mappingData.createdBy || localStorage.getItem('userEmail') || 'admin@emaildashboard.com'
        };

        return this.makeRequest({
            method: 'POST',
            url: '/cas/report/client-report/map/batch',
            data: requestData
        });
    }

    async updateReportMapping(clientId, reportCode, mappingData) {
        if (!clientId || !reportCode) {
            throw new Error('Client ID and Report Code are required');
        }
        
        const requestData = {
            isEnabled: mappingData.isEnabled !== undefined ? Boolean(mappingData.isEnabled) : true,
            updatedBy: mappingData.updatedBy || localStorage.getItem('userEmail') || 'admin@emaildashboard.com'
        };

        return this.makeRequest({
            method: 'PUT',
            url: `/cas/report/client-report/map/${clientId}/${reportCode}`,
            data: requestData
        });
    }

    async deleteReportMapping(clientId, reportCode) {
        if (!clientId || !reportCode) {
            throw new Error('Client ID and Report Code are required');
        }
        return this.makeRequest({
            method: 'DELETE',
            url: `/cas/report/client-report/map/${clientId}/${reportCode}`
        });
    }

    async batchDeleteReportMappings(requestData) {
        if (!requestData || !requestData.mappings || !Array.isArray(requestData.mappings) || requestData.mappings.length === 0) {
            throw new Error('Mappings array is required');
        }
        
        const validatedMappings = requestData.mappings.map(mapping => {
            if (!mapping.clientId || !mapping.reportCode) {
                throw new Error('Each mapping must have clientId and reportCode');
            }
            return {
                clientId: Number(mapping.clientId),
                reportCode: Number(mapping.reportCode)
            };
        });

        return this.makeRequest({
            method: 'POST',
            url: '/cas/report/client-report/map/batch-delete',
            data: {
                mappings: validatedMappings
            }
        });
    }

    async updateVerificationStatus(verificationData) {
        if (!verificationData || !verificationData.clientId || !verificationData.reportCode) {
            throw new Error('Client ID and Report Code are required');
        }
        
        const requestData = {
            clientId: Number(verificationData.clientId),
            reportCode: Number(verificationData.reportCode),
            isVerified: Boolean(verificationData.isVerified),
            hasError: Boolean(verificationData.hasError)
        };

        return this.makeRequest({
            method: 'POST',
            url: '/cas/report/client-report/map/update-verification',
            data: requestData
        });
    }

    async updateCronTriggerStatus(triggerData) {
        if (!triggerData || !triggerData.clientId || !triggerData.reportCode) {
            throw new Error('Client ID and Report Code are required');
        }
        
        const requestData = {
            clientId: Number(triggerData.clientId),
            reportCode: Number(triggerData.reportCode),
            isTriggered: Boolean(triggerData.isTriggered)
        };

        return this.makeRequest({
            method: 'POST',
            url: '/cas/report/client-report/map/update-cron-trigger',
            data: requestData
        });
    }

    async updateCronTriggerStatusByReport(reportCode, isTriggered) {
        if (!reportCode) {
            throw new Error('Report code is required');
        }
        
        const requestData = {
            reportCode: Number(reportCode),
            isTriggered: Boolean(isTriggered)
        };

        return this.makeRequest({
            method: 'POST',
            url: '/cas/report/client-report/map/update-cron-trigger-by-report',
            data: requestData
        });
    }

    async getReportMappings(params = {}) {
        return this.getReportMappingsList(params);
    }

    async createBatchReportMapping(mappingData) {
        return this.createBatchReportMappings(mappingData);
    }

    // ================================ UTILITY METHODS FOR REPORT MAPPING ================================

    /**
     * Validate mapping data structure before API calls
     * Ensures data matches backend ReportMappingRequest expectations
     */
    validateReportMappingData(mappingData, operation = 'create') {
        const errors = [];

        // Validation for create operations
        if (operation === 'create') {
            if (!mappingData.reportCode) {
                errors.push('Report code is required');
            }
            if (!mappingData.clientId) {
                errors.push('Client ID is required');
            }
        }

        // Validation for batch operations
        if (operation === 'batch') {
            if (!mappingData.reportCode) {
                errors.push('Report code is required');
            }
            if (!mappingData.clientIds || !Array.isArray(mappingData.clientIds) || mappingData.clientIds.length === 0) {
                errors.push('Client IDs array is required for batch operation');
            }
        }

        // Validation for update operations
        if (operation === 'update') {
            if (mappingData.isEnabled === undefined) {
                errors.push('Enabled status is required for update');
            }
            if (!mappingData.updatedBy) {
                errors.push('Updated by field is required');
            }
        }

        // Validation for verification operations
        if (operation === 'verification') {
            if (!mappingData.clientId) {
                errors.push('Client ID is required');
            }
            if (!mappingData.reportCode) {
                errors.push('Report code is required');
            }
            if (mappingData.isVerified === undefined) {
                errors.push('Verification status is required');
            }
            if (mappingData.hasError === undefined) {
                errors.push('Error status is required');
            }
        }

        return {
            isValid: errors.length === 0,
            errors: errors
        };
    }

    /**
     * Format mapping data for API requests
     * Ensures proper data types and structure matching backend expectations
     */
    formatReportMappingData(mappingData) {
        const formatted = { ...mappingData };

        // Convert string numbers to actual numbers
        if (formatted.reportCode !== undefined) {
            formatted.reportCode = Number(formatted.reportCode);
        }
        if (formatted.clientId !== undefined) {
            formatted.clientId = Number(formatted.clientId);
        }
        if (formatted.clientIds && Array.isArray(formatted.clientIds)) {
            formatted.clientIds = formatted.clientIds.map(id => Number(id));
        }

        // Ensure boolean values
        if (formatted.isEnabled !== undefined) {
            formatted.isEnabled = Boolean(formatted.isEnabled);
        }
        if (formatted.isVerified !== undefined) {
            formatted.isVerified = Boolean(formatted.isVerified);
        }
        if (formatted.hasError !== undefined) {
            formatted.hasError = Boolean(formatted.hasError);
        }
        if (formatted.isTriggered !== undefined) {
            formatted.isTriggered = Boolean(formatted.isTriggered);
        }

        // Set default user context if not provided
        if (!formatted.createdBy && (mappingData.reportCode || mappingData.clientId)) {
            formatted.createdBy = localStorage.getItem('userEmail') || 'admin@emaildashboard.com';
        }
        if (!formatted.updatedBy && (formatted.isEnabled !== undefined)) {
            formatted.updatedBy = localStorage.getItem('userEmail') || 'admin@emaildashboard.com';
        }

        return formatted;
    }

    /**
     * Helper method to build mapping query parameters
     * Standardizes parameter structure across mapping endpoints
     */
    buildMappingQueryParams(params = {}) {
        const queryParams = {};

        // Pagination parameters
        if (params.page !== undefined) {
            queryParams.page = Number(params.page) || 1;
        }
        if (params.size !== undefined) {
            queryParams.size = Number(params.size) || 20;
        }

        // Sorting parameters
        if (params.sortBy) {
            queryParams.sortBy = params.sortBy;
        }
        if (params.sortDirection) {
            queryParams.sortDirection = params.sortDirection;
        }

        // Search parameters
        if (params.search && params.search.trim()) {
            queryParams.search = params.search.trim();
        }

        // Filter parameters
        if (params.clientId) {
            queryParams.clientId = Number(params.clientId);
        }
        if (params.reportCode) {
            queryParams.reportCode = Number(params.reportCode);
        }

        return queryParams;
    }

    /**
     * Helper method to parse mapping response data
     * Standardizes response handling across mapping endpoints
     */
    parseReportMappingResponse(response) {
        if (!response || !response.data) {
            return null;
        }

        // Handle paginated responses
        if (response.data.content && Array.isArray(response.data.content)) {
            return {
                content: response.data.content,
                pagination: response.data.pagination || {
                    page: 1,
                    size: response.data.content.length,
                    total: response.data.content.length,
                    totalPages: 1
                }
            };
        }

        // Handle array responses
        if (Array.isArray(response.data)) {
            return {
                content: response.data,
                pagination: {
                    page: 1,
                    size: response.data.length,
                    total: response.data.length,
                    totalPages: 1
                }
            };
        }

        // Handle single object responses
        return response.data;
    }

    // ================================ REPORT RESULT API METHODS ================================

    async getReportResultList(params = {}) {
        return this.makeRequest({
            method: 'GET',
            url: '/cas/report/result/list',
            params: {
                page: params.page || 1,
                size: params.size || 20,
                sortBy: params.sortBy || 'executed_at_utc',
                sortDirection: params.sortDirection || 'DESC',
                search: params.search
            }
        });
    }

    async getReportResultsByClient(clientId, params = {}) {
        if (!clientId) {
            throw new Error('Client ID is required');
        }
        return this.makeRequest({
            method: 'GET',
            url: `/cas/report/result/client/${clientId}`,
            params: {
                page: params.page || 1,
                size: params.size || 20,
                search: params.search
            }
        });
    }

    async getReportResultsByReport(reportCode, params = {}) {
        if (!reportCode) {
            throw new Error('Report code is required');
        }
        return this.makeRequest({
            method: 'GET',
            url: `/cas/report/result/report/${reportCode}`,
            params: {
                page: params.page || 1,
                size: params.size || 20,
                search: params.search
            }
        });
    }

    async getReportResultsByStatus(status, params = {}) {
        if (!status) {
            throw new Error('Status is required');
        }
        return this.makeRequest({
            method: 'GET',
            url: `/cas/report/result/status/${status}`,
            params: {
                page: params.page || 1,
                size: params.size || 20,
                search: params.search
            }
        });
    }

    async getLatestReportResults(params = {}) {
        return this.makeRequest({
            method: 'GET',
            url: '/cas/report/result/latest',
            params: {
                page: params.page || 1,
                size: params.size || 20,
                search: params.search
            }
        });
    }

    async getReportResultStatistics() {
        return this.makeRequest({
            method: 'GET',
            url: '/cas/report/result/statistics'
        });
    }

    async getReportResult(clientId, reportCode) {
        if (!clientId || !reportCode) {
            throw new Error('Client ID and Report Code are required');
        }
        return this.makeRequest({
            method: 'GET',
            url: `/cas/report/result/${clientId}/${reportCode}`
        });
    }

    async saveReportResult(resultData) {
        if (!resultData || !resultData.clientId || !resultData.reportCode) {
            throw new Error('Client ID and Report Code are required');
        }
        
        const requestData = {
            clientId: Number(resultData.clientId),
            reportCode: Number(resultData.reportCode),
            executionStatus: resultData.executionStatus || 'pending',
            resultData: resultData.resultData,
            errorMessage: resultData.errorMessage,
            executedAtUtc: resultData.executedAtUtc
        };

        return this.makeRequest({
            method: 'POST',
            url: '/cas/report/result/',
            data: requestData
        });
    }

    async deleteReportResult(clientId, reportCode) {
        if (!clientId || !reportCode) {
            throw new Error('Client ID and Report Code are required');
        }
        return this.makeRequest({
            method: 'DELETE',
            url: `/cas/report/result/${clientId}/${reportCode}`
        });
    }

    async getAllResults(params = {}) {
        return this.getReportResults(params);
    }

    // ================================ ENHANCED ALERT API METHODS ================================

    async getAlerts(params = {}) {
        const config = this.addMicrosoftUserIdHeader({
            method: 'GET',
            url: '/cas/alert/list',
            params
        });
        
        return this.makeRequest(config);
    }

    async getLatestAlerts(limit = 3) {
        const config = this.addMicrosoftUserIdHeader({
            method: 'GET',
            url: '/cas/alert/latest',
            params: { limit }
        });
        
        return this.makeRequest(config);
    }

    async getAlertById(id) {
        const config = this.addMicrosoftUserIdHeader({
            method: 'GET',
            url: `/cas/alert/${id}`
        });
        
        return this.makeRequest(config);
    }

    async createAlert(alertData) {
        const config = this.addMicrosoftUserIdHeader({
            method: 'POST',
            url: '/cas/alert/',
            data: alertData
        });
        
        return this.makeRequest(config);
    }

    async deleteAlert(id) {
        const config = this.addMicrosoftUserIdHeader({
            method: 'DELETE',
            url: `/cas/alert/${id}`
        });
        
        return this.makeRequest(config);
    }

    async batchDeleteAlerts(alertIds) {
        const config = this.addMicrosoftUserIdHeader({
            method: 'DELETE',
            url: '/cas/alert/delete',
            data: { alertIds }
        });
        
        return this.makeRequest(config);
    }

    async getAlertStatistics() {
        const config = this.addMicrosoftUserIdHeader({
            method: 'GET',
            url: '/cas/alert/statistics'
        });
        
        return this.makeRequest(config);
    }

    /**
     * Get unread alert counts by client for specific user
     * Auto-includes X-Microsoft-User-Id header from storage
     * Matches: GET /cas/alert/user/unread-counts-by-client
     */
    async getUnreadAlertCountsByClientForUser(explicitMicrosoftUserId = null) {
        const config = this.addMicrosoftUserIdHeader({
            method: 'GET',
            url: '/cas/alert/user/unread-counts-by-client'
        }, explicitMicrosoftUserId);
        
        return this.makeRequest(config);
    }

    /**
     * Get alerts with user-specific read status using readStatus parameter
     * Auto-includes X-Microsoft-User-Id header from storage
     * Supports 3-state filtering with readStatus: "unread", "read", "all"
     */
    async getAlertsWithUserReadStatus(params = {}) {
        const queryParams = {
            page: params.page || 1,
            size: params.size || 20,
            sortBy: params.sortBy || 'createdAtUtc',
            sortDirection: params.sortDirection || 'desc'
        };

        // Only add these parameters if they have actual values
        if (params.search && params.search.trim()) {
            queryParams.search = params.search.trim();
        }
        if (params.category && params.category !== 'all') {
            queryParams.category = params.category;
        }
        if (params.status && params.status !== 'all') {
            queryParams.status = params.status;
        }
        if (params.clientId && params.clientId !== 'all') {
            queryParams.clientId = params.clientId;
        }
        
        // Use readStatus parameter directly instead of onlyUnread logic
        if (params.readStatus && params.readStatus !== 'all') {
            queryParams.readStatus = params.readStatus;
        }

        const config = this.addMicrosoftUserIdHeader({
            method: 'GET',
            url: '/cas/alert/user/list',
            params: queryParams
        });
        
        return this.makeRequest(config);
    }

    /**
     * Get latest alerts with user-specific read status
     * Auto-includes X-Microsoft-User-Id header from storage
     * Optimized for navbar notification components with personalized read status
     */
    async getLatestAlertsWithUserReadStatus(limit = 5, clientId = null, explicitMicrosoftUserId = null) {
        const params = { limit };
        if (clientId) {
            params.clientId = clientId;
        }

        const config = this.addMicrosoftUserIdHeader({
            method: 'GET',
            url: '/cas/alert/user/latest',
            params: params
        }, explicitMicrosoftUserId);
        
        return this.makeRequest(config);
    }

    /**
     * Mark alert as read for current user
     * Auto-includes X-Microsoft-User-Id header from storage
     * Automatically marks notification as read when viewed
     */
    async markAlertAsReadForUser(alertId, explicitMicrosoftUserId = null) {
        if (!alertId) {
            throw new Error('Alert ID is required');
        }

        const config = this.addMicrosoftUserIdHeader({
            method: 'POST',
            url: '/cas/alert/user/mark-read',
            data: { alertId }
        }, explicitMicrosoftUserId);
        
        return this.makeRequest(config);
    }


    // ================================ EVENT SCHEDULING API METHODS ================================
   
    /**
     * Get paginated list of event schedules with comprehensive filtering
     * Matches: GET /cas/event-schedule/
     */
    async getEventSchedules(params = {}) {
        return this.makeRequest({
            method: 'GET',
            url: '/cas/event-schedule/',
            params: {
                page: params.page || 1,
                size: params.size || 20,
                sortBy: params.sortBy || 'startDatetimeUtc',
                sortDirection: params.sortDirection || 'ASC',
                search: params.search,
                applicationId: params.applicationId,
                eventType: params.eventType,
                isCompleted: params.isCompleted,
                startDate: params.startDate,
                endDate: params.endDate
            }
        });
    }

    /**
     * Get event schedule by ID with complete details
     * Matches: GET /cas/event-schedule/{id}
     */
    async getEventScheduleById(eventId) {
        if (!eventId) {
            throw new Error('Event ID is required');
        }
        return this.makeRequest({
            method: 'GET',
            url: `/cas/event-schedule/${eventId}`
        });
    }

    /**
     * Get events for calendar view within date range
     * Matches: GET /cas/event-schedule/calendar
     */
    async getCalendarEvents(startDate, endDate) {
        if (!startDate || !endDate) {
            throw new Error('Start date and end date are required');
        }
        return this.makeRequest({
            method: 'GET',
            url: '/cas/event-schedule/calendar',
            params: {
                startDate: startDate,
                endDate: endDate
            }
        });
    }

    /**
     * Get upcoming events for dashboard widgets
     * Matches: GET /cas/event-schedule/upcoming
     */
    async getUpcomingEvents(days = 7, limit = 10) {
        return this.makeRequest({
            method: 'GET',
            url: '/cas/event-schedule/upcoming',
            params: {
                days: days,
                limit: limit
            }
        });
    }

    /**
     * Get available applications for event scheduling
     * Matches: GET /cas/event-schedule/applications
     */
    async getEventApplications() {
        return this.makeRequest({
            method: 'GET',
            url: '/cas/event-schedule/applications'
        });
    }

    /**
     * Get clients for specific application
     * Matches: GET /cas/event-schedule/applications/{id}/clients
     */
    async getEventApplicationClients(applicationId) {
        if (!applicationId) {
            throw new Error('Application ID is required');
        }
        return this.makeRequest({
            method: 'GET',
            url: `/cas/event-schedule/applications/${applicationId}/clients`
        });
    }

    /**
     * Get available users for event assignment
     * Matches: GET /cas/event-schedule/users
     */
    async getEventAvailableUsers() {
        return this.makeRequest({
            method: 'GET',
            url: '/cas/event-schedule/users'
        });
    }

    /**
     * Get events assigned to current user
     * Matches: GET /cas/event-schedule/user/assigned
     */
    async getUserAssignedEvents(params = {}) {
        return this.makeRequest({
            method: 'GET',
            url: '/cas/event-schedule/user/assigned',
            params: {
                page: params.page || 1,
                size: params.size || 20,
                sortBy: params.sortBy || 'startDatetimeUtc',
                sortDirection: params.sortDirection || 'ASC'
            }
        });
    }

    /**
     * Get event scheduling statistics for dashboard
     * Matches: GET /cas/event-schedule/statistics
     */
    async getEventStatistics() {
        return this.makeRequest({
            method: 'GET',
            url: '/cas/event-schedule/statistics'
        });
    }

    // ================================ EVENT CREATION AND MODIFICATION ================================

    /**
     * Create new event schedule with validation and email notification
     * Matches: POST /cas/event-schedule/
     */
    async createEventSchedule(eventData) {
        if (!eventData || !eventData.eventTitle || !eventData.applicationId || !eventData.eventType) {
            throw new Error('Event title, application ID, and event type are required');
        }

        const validatedData = this.validateEventScheduleData(eventData);

        return this.makeRequest({
            method: 'POST',
            url: '/cas/event-schedule/',
            data: {
                eventTitle: validatedData.eventTitle,
                eventDescription: validatedData.eventDescription,
                applicationId: parseInt(validatedData.applicationId),
                eventType: validatedData.eventType,
                startDatetimeUtc: validatedData.startDatetimeUtc,
                endDatetimeUtc: validatedData.endDatetimeUtc,
                toAddresses: validatedData.toAddresses,
                ccAddresses: validatedData.ccAddresses,
                bccAddresses: validatedData.bccAddresses,
                emailSubject: validatedData.emailSubject,
                emailBody: validatedData.emailBody,
                selectedClients: validatedData.selectedClients,
                assignedUsers: validatedData.assignedUsers
            }
        });
    }

    /**
     * Update existing event schedule
     * Matches: PUT /cas/event-schedule/{id}
     */
    async updateEventSchedule(eventId, eventData) {
        if (!eventId) {
            throw new Error('Event ID is required');
        }

        const validatedData = this.validateEventScheduleData(eventData);

        return this.makeRequest({
            method: 'PUT',
            url: `/cas/event-schedule/${eventId}`,
            data: {
                eventTitle: validatedData.eventTitle,
                eventDescription: validatedData.eventDescription,
                applicationId: parseInt(validatedData.applicationId),
                eventType: validatedData.eventType,
                startDatetimeUtc: validatedData.startDatetimeUtc,
                endDatetimeUtc: validatedData.endDatetimeUtc,
                toAddresses: validatedData.toAddresses,
                ccAddresses: validatedData.ccAddresses,
                bccAddresses: validatedData.bccAddresses,
                emailSubject: validatedData.emailSubject,
                emailBody: validatedData.emailBody,
                selectedClients: validatedData.selectedClients,
                assignedUsers: validatedData.assignedUsers
            }
        });
    }

    /**
     * Mark event as completed (Admin/DevOps only)
     * Matches: POST /cas/event-schedule/{id}/complete
     */
    async markEventAsCompleted(eventId) {
        if (!eventId) {
            throw new Error('Event ID is required');
        }
        return this.makeRequest({
            method: 'POST',
            url: `/cas/event-schedule/${eventId}/complete`
        });
    }

    /**
     * Send event notification email manually
     * Matches: POST /cas/event-schedule/{id}/notify
     */
    async sendEventNotification(eventId) {
        if (!eventId) {
            throw new Error('Event ID is required');
        }
        return this.makeRequest({
            method: 'POST',
            url: `/cas/event-schedule/${eventId}/notify`
        });
    }

    /**
     * Delete event schedule (soft delete)
     * Matches: DELETE /cas/event-schedule/{id}
     */
    async deleteEventSchedule(eventId) {
        if (!eventId) {
            throw new Error('Event ID is required');
        }
        return this.makeRequest({
            method: 'DELETE',
            url: `/cas/event-schedule/${eventId}`
        });
    }

    async updateEventDevopsStatus(data){
        console.log(data.eventId);
        console.log(data.devopsStatus);
        var url=`/cas/event-schedule/updateDevopsStatus`
        return this.makeRequest({
            method: 'POST',
            url: url,
            data:{
                id: data.eventId,
                devopsStatus: data.devopsStatus,
                devopsMessage: data.devopsMessage,
                updatedby: data.updatedBy
            }
        })
    }

    // ================================ EVENT VALIDATION AND CONFLICT CHECKING ================================

    /**
     * Validate event schedule data before creation/update
     * Matches: POST /cas/event-schedule/validate
     */
    async validateEventSchedule(eventData, excludeEventId = null) {
        const validatedData = this.validateEventScheduleData(eventData);

        return this.makeRequest({
            method: 'POST',
            url: '/cas/event-schedule/validate',
            data: {
                eventSchedule: validatedData,
                excludeEventId: excludeEventId
            }
        });
    }

    /**
     * Check for event conflicts in time range
     * Matches: POST /cas/event-schedule/conflicts
     */
    async checkEventConflicts(startDatetime, endDatetime, applicationId, excludeEventId = null) {
        if (!startDatetime || !endDatetime) {
            throw new Error('Start datetime and end datetime are required');
        }

        return this.makeRequest({
            method: 'POST',
            url: '/cas/event-schedule/conflicts',
            data: {
                startDatetime: startDatetime,
                endDatetime: endDatetime,
                applicationId: applicationId ? parseInt(applicationId) : null,
                excludeEventId: excludeEventId
            }
        });
    }

    // ================================ UTILITY AND HELPER METHODS ================================

    /**
     * Validate and format event schedule data for API requests
     * Ensures proper data types and required fields
     */
    validateEventScheduleData(eventData) {
        const errors = [];

        if (!eventData.eventTitle || eventData.eventTitle.trim() === '') {
            errors.push('Event title is required');
        }

        if (!eventData.applicationId) {
            errors.push('Application ID is required');
        }

        if (!eventData.eventType) {
            errors.push('Event type is required');
        }

        if (!['Deployment', 'Upgrade', 'Maintenance'].includes(eventData.eventType)) {
            errors.push('Event type must be Deployment, Upgrade, or Maintenance');
        }

        if (!eventData.startDatetimeUtc) {
            errors.push('Start datetime is required');
        }

        if (!eventData.endDatetimeUtc) {
            errors.push('End datetime is required');
        }

        if (eventData.startDatetimeUtc && eventData.endDatetimeUtc) {
            const startDate = new Date(eventData.startDatetimeUtc);
            const endDate = new Date(eventData.endDatetimeUtc);
            
            if (startDate >= endDate) {
                errors.push('Start datetime must be before end datetime');
            }
        }

        // if (!eventData.emailSubject || eventData.emailSubject.trim() === '') {
        //     errors.push('Email subject is required');
        // }

        // if (!eventData.emailBody || eventData.emailBody.trim() === '') {
        //     errors.push('Email body is required');
        // }

        // if (!eventData.toAddresses || 
        //     (typeof eventData.toAddresses === 'string' && eventData.toAddresses.trim() === '') ||
        //     (Array.isArray(eventData.toAddresses) && eventData.toAddresses.length === 0)) {
        //     errors.push('At least one recipient email is required');
        // }

        if (!eventData.selectedClients || 
            (typeof eventData.selectedClients === 'string' && eventData.selectedClients.trim() === '') ||
            (Array.isArray(eventData.selectedClients) && eventData.selectedClients.length === 0)) {
            errors.push('At least one client must be selected');
        }

        if (!eventData.assignedUsers || 
            (typeof eventData.assignedUsers === 'string' && eventData.assignedUsers.trim() === '') ||
            (Array.isArray(eventData.assignedUsers) && eventData.assignedUsers.length === 0)) {
            errors.push('At least one user must be assigned');
        }

        if (errors.length > 0) {
            throw new Error(`Event validation failed: ${errors.join(', ')}`);
        }

        return this.formatEventScheduleData(eventData);
    }

    /**
     * Format event schedule data for API requests
     * Ensures proper data types and structure
     */
    formatEventScheduleData(eventData) {
        const formatted = { ...eventData };

        // Ensure proper data types
        if (formatted.applicationId !== undefined) {
            formatted.applicationId = parseInt(formatted.applicationId);
        }

        // Format datetime fields to ISO string
        if (formatted.startDatetimeUtc && typeof formatted.startDatetimeUtc !== 'string') {
            formatted.startDatetimeUtc = new Date(formatted.startDatetimeUtc).toISOString();
        }

        if (formatted.endDatetimeUtc && typeof formatted.endDatetimeUtc !== 'string') {
            formatted.endDatetimeUtc = new Date(formatted.endDatetimeUtc).toISOString();
        }

        // Format email addresses to JSON string if array
        if (Array.isArray(formatted.toAddresses)) {
            formatted.toAddresses = JSON.stringify(formatted.toAddresses);
        }

        if (Array.isArray(formatted.ccAddresses)) {
            formatted.ccAddresses = JSON.stringify(formatted.ccAddresses);
        }

        if (Array.isArray(formatted.bccAddresses)) {
            formatted.bccAddresses = JSON.stringify(formatted.bccAddresses);
        }

        // Format client and user selections to JSON string if array
        if (Array.isArray(formatted.selectedClients)) {
            formatted.selectedClients = JSON.stringify(formatted.selectedClients);
        }

        if (Array.isArray(formatted.assignedUsers)) {
            formatted.assignedUsers = JSON.stringify(formatted.assignedUsers);
        }

        // Trim string fields
        const stringFields = ['eventTitle', 'eventDescription', 'emailSubject', 'emailBody'];
        stringFields.forEach(field => {
            if (formatted[field] && typeof formatted[field] === 'string') {
                formatted[field] = formatted[field].trim();
            }
        });

        return formatted;
    }

    /**
     * Parse email addresses from various formats
     * Handles both comma-separated strings and JSON arrays
     */
    parseEmailAddresses(emailData) {
        if (!emailData) return [];

        try {
            if (typeof emailData === 'string') {
                if (emailData.trim().startsWith('[')) {
                    return JSON.parse(emailData);
                } else {
                    return emailData.split(/[,;]/).map(email => email.trim()).filter(email => email);
                }
            } else if (Array.isArray(emailData)) {
                return emailData;
            }
        } catch (error) {
            console.warn('Failed to parse email addresses:', emailData, error);
        }

        return [];
    }

    /**
     * Parse JSON array from string format
     * Safe parsing with fallback for malformed JSON
     */
    parseJsonArray(jsonData) {
        if (!jsonData) return [];

        try {
            if (typeof jsonData === 'string') {
                return JSON.parse(jsonData);
            } else if (Array.isArray(jsonData)) {
                return jsonData;
            }
        } catch (error) {
            console.warn('Failed to parse JSON array:', jsonData, error);
        }

        return [];
    }

    /**
     * Format date for display in event components
     * Consistent date formatting across the application
     */
    formatEventDate(dateString) {
        if (!dateString) return '';

        try {
            const date = new Date(dateString);
            return date.toLocaleString('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
                timeZone: 'UTC'
            }) + ' UTC';
        } catch (error) {
            console.warn('Failed to format date:', dateString, error);
            return dateString;
        }
    }

    /**
     * Get event type color for UI display
     * Consistent color scheme across calendar and list views
     */
    getEventTypeColor(eventType, isCompleted = false) {
        if (isCompleted) {
            return '#38A169'; // green.500
        }

        const colors = {
            'Deployment': '#3182CE', // blue.500
            'Upgrade': '#D69E2E',     // yellow.500
            'Maintenance': '#E53E3E'   // red.500
        };

        return colors[eventType] || '#718096'; // gray.500
    }

    /**
     * Build query parameters for event filtering
     * Standardizes parameter structure across event endpoints
     */
    buildEventQueryParams(params = {}) {
        const queryParams = {};

        // Pagination parameters
        if (params.page !== undefined) {
            queryParams.page = Number(params.page) || 1;
        }
        if (params.size !== undefined) {
            queryParams.size = Number(params.size) || 20;
        }

        // Sorting parameters
        if (params.sortBy) {
            queryParams.sortBy = params.sortBy;
        }
        if (params.sortDirection) {
            queryParams.sortDirection = params.sortDirection;
        }

        // Search parameters
        if (params.search && params.search.trim()) {
            queryParams.search = params.search.trim();
        }

        // Filter parameters
        if (params.applicationId) {
            queryParams.applicationId = Number(params.applicationId);
        }
        if (params.eventType) {
            queryParams.eventType = params.eventType;
        }
        if (params.isCompleted !== undefined) {
            queryParams.isCompleted = Boolean(params.isCompleted);
        }

        // Date range parameters
        if (params.startDate) {
            queryParams.startDate = params.startDate;
        }
        if (params.endDate) {
            queryParams.endDate = params.endDate;
        }

        return queryParams;
    }

    // ================================ INTEGRATION HELPER METHODS ================================

    /**
     * Get events for React Big Calendar format
     * Transforms API response for calendar display
     */
    async getCalendarEventsFormatted(startDate, endDate) {
        try {
            const response = await this.getCalendarEvents(startDate, endDate);
            
            if (!response?.success || !response?.data) {
                return [];
            }

            return response.data.map(event => ({
                id: event.id,
                title: event.eventTitle,
                start: new Date(event.startDatetimeUtc),
                end: new Date(event.endDatetimeUtc),
                resource: {
                    ...event,
                    color: this.getEventTypeColor(event.eventType, event.isCompleted),
                    textColor: '#FFFFFF'
                }
            }));

        } catch (error) {
            console.error('Error fetching formatted calendar events:', error);
            return [];
        }
    }

    /**
     * Export event to calendar format (ICS)
     * Generates calendar file for external calendar applications
     */
    generateEventICS(event) {
        if (!event) return null;

        const formatICSDate = (date) => {
            return new Date(date).toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
        };

        const escapeICSText = (text) => {
            return text.replace(/\\/g, '\\\\')
                      .replace(/;/g, '\\;')
                      .replace(/,/g, '\\,')
                      .replace(/\n/g, '\\n');
        };

        const icsContent = [
            'BEGIN:VCALENDAR',
            'VERSION:2.0',
            'PRODID:-//Email Dashboard//Event Scheduling//EN',
            'BEGIN:VEVENT',
            `UID:event-${event.id}@emaildashboard.com`,
            `DTSTART:${formatICSDate(event.startDatetimeUtc)}`,
            `DTEND:${formatICSDate(event.endDatetimeUtc)}`,
            `SUMMARY:${escapeICSText(event.eventTitle)}`,
            `DESCRIPTION:${escapeICSText(event.eventDescription || '')}`,
            `LOCATION:${escapeICSText(event.applicationName || '')}`,
            `CATEGORIES:${event.eventType}`,
            `STATUS:${event.isCompleted ? 'COMPLETED' : 'CONFIRMED'}`,
            'END:VEVENT',
            'END:VCALENDAR'
        ].join('\r\n');

        return icsContent;
    }

    // ================================ SEARCH API METHODS ================================

    async globalSearch(query, params = {}) {
        return this.makeRequest({
            method: 'GET',
            url: '/cas/search',
            params: { q: query, ...params }
        });
    }

    async searchReports(query, params = {}) {
        return this.makeRequest({
            method: 'GET',
            url: '/cas/search/reports',
            params: { q: query, ...params }
        });
    }

    // ================================ SYSTEM API METHODS ================================

    async getSystemInfo() {
        return this.makeRequest({
            method: 'GET',
            url: '/cas/system/info'
        });
    }

    async getSystemHealth() {
        return this.makeRequest({
            method: 'GET',
            url: '/cas/health'
        });
    }

    async fetchClientOfflineHistory(clientId){
        return this.makeRequest({
            method: 'GET',
            url: `/cas/dashboard/heartbeat-history?clientId=${clientId}`
        })
    }

    async fetchClientClickHistory(clientId){
        return this.makeRequest({
            method: 'GET',
            url: `/cas/dashboard/click-history?clientId=${clientId}`
        })
    }


    /**
     * Method to Create/Register Client Application
     */
    async createClientApplication(appData) {
        console.log(appData);
        if (!appData || !appData.name || !appData.type) {
            throw new Error('Application Name and Application type Required');
        }
        return this.makeRequest({
            method: 'POST',
            url: '/cas/application/create-application',
            data: {
                appName: appData.name,
                appType: appData.type,
                appDescription: appData.description,
                appStatus: appData.status,
                appVersion:appData.version,
                minimalVersion:appData.minimalVersion
            }
        });
    }

    async updateClientApplication(appData){
        if(!appData|| !appData.id){
            throw new Error('Application Id is Required');
        }
        return this.makeRequest({
            method:'POST',
            url:'/cas/application/update-application',
            data:{
                appId:appData.id,
                appName: appData.name,
                appType: appData.type,
                appDescription: appData.description,
                appStatus: appData.status,
                appVersion:appData.version,
                minimalVersion:appData.minimalVersion
            }
        })
    }

    async deleteClientApplication(appData){
        if(!appData || !appData.appId){
            throw new Error("Application Id is required ");
        }
        return this.makeRequest({
            method:'POST',
            url:'/cas/application/deleteClientApplication',
            data:{
                appId:appData.appId,
                updatedBy: appData.updatedBy
            }
        })
    }

    async fetchClientApplications(){
        return this.makeRequest({
            method:'GET',
            url: '/cas/application/list'
        })
    }

    async fetchAvailableApplicationsForClient(clientId){
       return this.makeRequest({
            method: 'GET',
            url: `/cas/application/getAvailableApplications?clientId=${clientId}`
        })
    }

    async createClientApplicationMapping(appData){
        return this.makeRequest({
            method: 'POST',
            url : '/cas/application/mapAppstoClient',
            data:{
                clientId: appData.clientId,
                appIds: appData.applicationIds,
                createdBy: appData.createdBy,
                isEnabled: true,
                mappingVersion:appData.mappingVersion
            }
        })
    }

    async fetchClientApplicationMappings(appData){
        var url='/cas/application/fetchClientApplicationMappings'
        if(appData.clientId && appData.applicationId){
            url=`/cas/application/fetchClientApplicationMappings?clientId=${appData.clientId}&appId=${appData.applicationId}`;
        }
        else if(appData.clientId){
            url=`/cas/application/fetchClientApplicationMappings?clientId=${appData.clientId}`;
        }
        else if(appData.applicationId){
            url=`/cas/application/fetchClientApplicationMappings?appId=${appData.applicationId}`;
        }
        return this.makeRequest({
            method: 'GET',
            url : url
        })
    }

    async updateClientApplicationMapping(appData){
        return this.makeRequest({
            method:'POST',
            url:'/cas/application/updateClientApplicationMapping',
            data:{
                clientId:appData.clientId,
                appId:appData.applicationId,
                updatedBy:appData.updatedBy,
                isEnabled:appData.isEnabled,
                mappingVersion:appData.mappingVersion
            }
        })
    }

    async deleteClientApplicationMapping(appData){
        return this.makeRequest({
            method : 'POST',
            url: '/cas/application/deleteClientApplicationMapping',
            data :{
                clientId:appData.clientId,
                appId: appData.appId,
                updatedBy:appData.updatedBy
            }
        })
    }

    async fetchClientProfileDetails(clientId){
        var url=`/cas/client/clientProfile?clientId=${clientId}`
        return this.makeRequest({
            method: 'GET',
            url : url
        })
    }

    async fetchSupportPersons(clientId){
        var url=`/cas/client/supportPersons?clientId=${clientId}`
        return this.makeRequest({
            method:'GET',
            url: url
        })
    }

    async addSupportPerson(clientId, person){
        var url=`/cas/client/createSupportPerson`
        return this.makeRequest({
            method: 'POST',
            url: url,
            data:{
                clientId: clientId,
                name: person.name,
                email:person.email,
                isLead: person.isLead,
                team:person.team
            }
        })
    }
    async updateSupportPerson(clientId, id, person){
        var url=`/cas/client/updateSupportPerson`
        return this.makeRequest({
            method: 'POST',
            url: url,
            data:{
                id: id,
                clientId: clientId,
                name: person.name,
                email:person.email,
                isLead: person.isLead,
                team:person.team
            }
        })
    }

    async deleteSupportPerson(clientId, id){
        var url=`/cas/client/deleteSupportPerson`
        return this.makeRequest({
            method: 'POST',
            url: url,
            data:{
                id:id,
                clientId: clientId
            }
        })
    }

    async createUrlMonitor(data){
        var url=`/cas/urlmonitor/addurl`
        return this.makeRequest({
            method:'POST',
            url: url,
            data:{
                url: data.url,
                status: data.status,
                clientId: data.clientId
            }
        })
    }

    async updateUrlMonitor(data){
        var url=`/cas/urlmonitor/updateurl`
        return this.makeRequest({
            method:'POST',
            url: url,
            data:{
                id: data.id,
                url: data.url,
                status: data.status,
                clientId: data.clientId
            }
        })
    }

    async deleteUrlMonitor(id){
        var url='/cas/urlmonitor/deleteurl'
        return this.makeRequest({
            method: 'POST',
            url: url,
            data:{
                id: id
            }
        })
    }

    async fetchUrlMonitors(){
        var url='/cas/urlmonitor/list'
        return this.makeRequest({
            method:'GET',
            url: url,
        })
    }
    async fetchUrlMonitorsByClient(clientId){
        var url=`/cas/urlmonitor/list?clientId=${clientId}`
        return this.makeRequest({
            method:'GET',
            url: url
        })
    }

    async createCheckList(data){
        var url=`/cas/checklist/addCheckList`
        return this.makeRequest({
            method:'POST',
            url: url,
            data: {
                name: data.name,
                description:data.description,
                checkListQuery: data.checkListQuery,
                updated_by: data.updatedBy
            }
        })
    }

    async updateChecklist(data){
        var url=`/cas/checklist/updateCheckList`
        return this.makeRequest({
            method:'POST',
            url: url,
            data: {
                id: data.id,
                name: data.name,
                description:data.description,
                checkListQuery: String(data.checkListQuery || ""),
                updated_by: data.updatedBy
            }
        })
    }
    async deleteChecklist(id){
        return this.makeRequest({
            method:'POST',
            url: `/cas/checklist/deleteCheckList`,
            data:{
                id: id
            }
        })
    }

    async deleteChecklistMapping(id){
        return this.makeRequest({
            method:"POST",
            url:`/cas/checklist/deleteMappings`,
            data:{
                id:id
            }
        })
    }

    async createChecklistMapping(data){
        var url=`/cas/checklist/createMappings`
        return this.makeRequest({
            method:"POST",
            url: url,
            data:{
                checklistId: data.checklistId,
                clientId: data.clientId,
                scheduledTime: data.scheduledTime,
                updatedBy:data.updatedBy,
                timezone:data.timezone
            }
        })
    }

    async createDynamicReport(data){
        var url='/cas/dynamicreports/create'
        return this.makeRequest({
            method: "POST",
            url: url,
            data:{
                name:       data.name,
                description: data.description,
                reportQuery:       data.reportQuery,
                updatedBy:         data.updatedBy,
            }
        })
    }

    async updateDynamicReport(data){
        var url='/cas/dynamicreports/update'
        return this.makeRequest({
            method: "POST",
            url: url,
            data:{
                id: data.id,
                name:       data.name,
                description: data.description,
                reportQuery:       data.reportQuery,
                updatedBy:         data.updatedBy,
            }
        })
    }
    async deleteDynamicReport(id){
        var url='/cas/dynamicreports/delete'
        return this.makeRequest({
            method:"POST",
            url: url,
            data:{
                id: id
            }
        })
    }
    async fetchDynamicReports(){
        return this.makeRequest({
            method:"GET",
            url: '/cas/dynamicreports/list'
        })
    }

    async updateChecklistMapping(id, data){
        var url=`/cas/checklist/updateMappings`
        return this.makeRequest({
            method:"POST",
            url: url,
            data:{
                id:id,
                checklistId: data.checklistId,
                clientId: data.clientId,
                scheduledTime: data.scheduledTime,
                updatedBy:data.updatedBy,
                timezone:data.timezone
            }
        })
    }


    async fetchChecklists(){
        return this.makeRequest({
            method:'GET',
            url: '/cas/checklist/list'
        })
    }

    async getChecklistMappings(){
        return this.makeRequest({
            method:'GET',
            url:'/cas/checklist/listmappings'
        })
    }

    async getMappingsbyChecklist(){
        return this.makeRequest({
            method:'GET',
            url:`/cas/checklist/getMappingsByChecklist?checklistId=${3}`
        })
    }

    async fetchChecklistResults(){
        return this.makeRequest({
            method:'GET',
            url:'/cas/checklist/results'
        })
    }
    async getMarketDataResult(){
        return this.makeRequest({
            method:'GET',
            url:'/cas/checklist/etlresults'
        })
    }
    async reloadMarketDataResult(updatedBy){
        return this.makeRequest({
            method:"POST",
            url:`/cas/checklist/result/reloadetlresults`,
            data:{
                updatedBy: updatedBy
            }
        })
    }

    async getDynamicReportMappings(){
        return this.makeRequest({
            method:'GET',
            url:'/cas/dynamicreports/listmappings'
        })
    }

    async createDynamicReportMapping(data){
        return this.makeRequest({
            method:"POST",
            url: "/cas/dynamicreports/addMappings",
            data:{
                reportId: data.reportId,
                clientId: data.clientId,
                updatedBy: data.updatedBy
            }
        })
    }

    async createDynamicReportMappingsBatch(data){
        return this.makeRequest({
            method:"POST",
            url: "/cas/dynamicreports/addBatchMappings",
            data:{
                reportId: data.reportId,
                clientIds: data.clientIds,
                updatedBy: data.updatedBy
            }
        })
    }

    async updateDynamicReportMapping(id, data){
        return this.makeRequest({
            method:"POST",
            url: "/cas/dynamicreports/updateMappings",
            data:{
                id: id,
                reportId: data.reportId,
                clientId: data.clientId,
                updatedBy: data.updatedBy
            }
        })
    }

    async deleteDynamicReportMapping(id){
        return this.makeRequest({
            method:"POST",
            url:"/cas/dynamicreports/deleteMappings",
            data:{
                id: id
            }
        })
    }

    async reloadChecklistClient(clientId){
        return this.makeRequest({
            method:"POST",
            url: '/cas/checklist/result/reload',
            data:{
                clientId : clientId
            }
        })
    }

    async fetchDynamicReportResults(reportId){
        var url= `/cas/dynamicreports/getresultsbyReport?reportId=${reportId}`
        return this.makeRequest({
            method:"GET",
            url: url
        })
    }

    // Trigger a one-time, on-demand execution of an issue's data query (server resolves
    // the authoritative query from the issue's report and runs it via the reload channel).
    async runIssueDataQuery(issueId){
        return this.makeRequest({
            method: 'POST',
            url: '/cas/dynamicreports/runIssueDataQuery',
            params: { issueId }
        });
    }

    // Fetch the latest stored result for an issue's data query.
    async getIssueDataQueryResult(issueId){
        return this.makeRequest({
            method: 'GET',
            url: '/cas/dynamicreports/issueDataQueryResult',
            params: { issueId }
        });
    }

    async reloadDynamicReportResultsbyReport(reportId){
        var url=`/cas/dynamicreports/reloadResults`
        return this.makeRequest({
            method:"POST",
            url: url,
            data:{
                reportId: reportId
            }
        })
    }
    /**
     * Cleanup method to remove interceptors
     * Should be called when service is no longer needed
     */
    cleanup() {
        if (this.axiosInstance && this.requestInterceptor) {
            this.axiosInstance.interceptors.request.eject(this.requestInterceptor);
        }
        if (this.axiosInstance && this.responseInterceptor) {
            this.axiosInstance.interceptors.response.eject(this.responseInterceptor);
        }
    }
}

// Create singleton instance
const apiService = new ApiService();

export default apiService;